<template>
  <div v-if="notesReady" class="notes-overview">
    <div class="header-actions">
      <div v-if="notes.length > 0" class="search">
        <k-text-search v-model="filterText"></k-text-search>
      </div>
      <div v-if="filterable" class="filter">
        <k-dropdown-multi-select
          v-model="selectedProjectPhases"
          :options="projectPhaseOptions"
          placeholder="Filter by project phase"
          :showSearch="false"
        >
        </k-dropdown-multi-select>
      </div>
      <div v-if="notes.length > 0" class="sort">
        <button class="btn btn-outlined btn-sort" @click="toggleSort">
          <span>
            Sort
            <i class="fas" :class=" sortDescending ? 'fa-sort-amount-down-alt' : 'fa-sort-amount-up'" aria-hidden="true"></i>
          </span>
        </button>
      </div>
      <div v-if="hasInteractionPermission" class="header-buttons">
        <button v-if="!addingNewNote && allowAddingNewNotes" class="btn btn-primary add-new" @click="addingNewNote = !addingNewNote">
          Add new <i class="fa-solid fa-plus" aria-hidden="true"></i>
        </button>
        <button v-if="addingNewNote" class="btn btn-default cancel-btn" @click="addingNewNote = false">
          Cancel
        </button>
        <button v-if="addingNewNote && !savingNote" class="btn btn-success save-btn" @click="addNewNote" :disabled="disableSave">
          Save new note <i class="fa-solid fa-check" aria-hidden="true"></i>
        </button>
        <span class="btn btn-loading" v-else-if="savingNote" disabled>
          Saving <i class="fa-duotone fa-spinner fa-spin" aria-hidden="true"></i>
        </span>
      </div>
    </div>
    <div class="main-notes-container">
      <div class="note-editor" :class="addingNewNote ? 'showing-editor' : 'hidden-editor'" >
        <div>
          <label  v-if="!isProjectFeedback" class="k-custom-checkbox share-note-check">
            <input type="checkbox" v-model="shareWithEmployer" class="hidden-checkbox"/>
            <span class="k-custom-checkbox-box light ph-no-capture"></span>
            <span class="k-custom-checkbox-text">Share note with employer</span>
          </label>
          <k-text-editor v-model="body" :maxLength="editorMaxLength" :posthog-no-capture="posthogNoCapture"></k-text-editor>
        </div>
      </div>
      <div class="notes-list" v-if="notes.length > 0">
        <note v-for="note in filteredNotes" :key="note.note_id"
          :userId="userId"
          :programmeId="programmeId"
          :note="note"
          @openEditModal="openEditModal(note)"
          :filterText="filterText"
          :hasInteractionPermission="hasInteractionPermission"
          :use-custom-notes="useCustomNotes"
          @custom-delete-note="handleDeleteNote"
          @update="getNotes()"
          :posthog-no-capture="posthogNoCapture"
        >
          <template #tags>
            <slot name="tags" :note="note"></slot>
          </template>
        </note>
        <div v-if="maxRows < notes.length" class="show-more">
          <button v-if="totalRows < notes.length && filteredNotes.length >= totalRows"  @click="additionalRows += 5">
            <span>Show more</span>
            <i class="fas fa-angle-down" aria-hidden="true"></i>
          </button>
          <button v-if="totalRows > 8" @click="additionalRows -= 5">
            <span>Show less</span>
            <i class="fas fa-angle-up" aria-hidden="true"></i>
          </button>
        </div>
      </div>
      <div class="empty-note-placeholder" v-else>
        <span>No notes found for this {{ isProjectFeedback ? 'project' : 'user' }}.</span>
      </div>
    </div>
    <edit-note
      class="edite-note-modal"
      @close="close"
      @edited="getNotes()"
      :show="showEditModal"
      :note="editNote"
      :userId="userId"
      :programmeId="programmeId"
      :use-custom-notes="useCustomNotes"
      @custom-edit-note="handleEditNote"
    >
    </edit-note>
  </div>
  <div v-else class="content-spinner" role="status" aria-label="Loading">
    <i class="fa-duotone fa-spinner fa-spin" aria-hidden="true"></i>
  </div>
</template>

<style>
.notes-overview .header-actions .search input.form-control {
  padding: 5px 5px 5px 30px;
}

.notes-overview .k-search-dropdown-adv-content {
  min-width: 180px;
  left: 50%;
  transform: translateX(-50%);
}

.notes-overview .k-search-dropdown-menu .btn-primary {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* K-Editor */
.main-notes-container .note-editor #k-text-editor .custom-editor {
  margin: 0;
}

.main-notes-container .note-editor.hidden-editor {
  width: 0;
  height: 0;
}

.main-notes-container .note-editor.hidden-editor > div {
  opacity: 0;
  max-height: 0;
  margin: 0;
  pointer-events: none;
  transition: opacity 0.2s ease-in, max-height 0.25s;
}

.main-notes-container .note-editor.showing-editor > div {
  opacity: 1;
  max-height: 800px;
  margin-bottom: 25px;
  overflow: auto;
  pointer-events: unset;
  transition: opacity 0.2s ease-in, max-height 0.25s;
}

.notes-overview .share-note-check {
  cursor: pointer;
  margin: 15px 0;
  width: fit-content;
  float: right;
}

.notes-overview .filter .k-search-dropdown-button {
  padding: 8px 12px;
}
</style>

<style scoped>
.notes-overview {
  position: relative;
}

.header-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: right;
  align-items: center;
  gap: 15px;
}

.header-buttons .btn {
  margin-right: 8px;
}

.btn-sort:focus span {
  mix-blend-mode: difference;
}

.main-notes-container {
  margin-top: 20px;
  height: 300px;
  overflow-y: auto;
}

@media (max-width: 820px) {
  .header-actions {
    justify-content: space-evenly;
  }
}

.show-more {
  display: flex;
  align-items: center;
}

.show-more > button {
  display: flex;
  gap: 5px;
  align-items: center;
  width: 30%;
  background-color: transparent;
  color: var(--kate-primary);
  margin: 0 15px;
}

.show-more > button:hover {
  color: var(--kate-secondary);
}

.show-more > button > span {
  text-decoration: underline;
}
</style>

<script>
import TimeMixin from '@mixins/time-mixins';
import KTextSearch from '@base-components/k-text-search.vue';
import KTextEditor from '@base-components/k-text-editor.vue';
import KDropdownMultiSelect from '@base-components/k-dropdown-multi-select.vue';
import { sortObjectArray } from '@utils/sort-by-object-property';
import EditNote from './student_components/edit-note.vue';
import Note from './student_components/note.vue';

export default {
  components: {
    KTextSearch,
    Note,
    KTextEditor,
    KDropdownMultiSelect,
    EditNote,
  },

  props: {
    userId: {
      type: Number,
      default: null,
    },
    programmeId: {
      type: Number,
      default: null,
    },
    isLearnerSupport: Boolean,
    maxRows: {
      type: Number,
      default: 5,
    },
    isProjectFeedback: {
      type: Boolean,
      default: false,
    },
    filterable: {
      type: Boolean,
      default: false,
    },
    useCustomNotes: {
      type: Boolean,
      default: false,
    },
    customNotes: {
      type: Array,
      default: undefined,
    },
    customSortBy: {
      type: String,
      default: undefined,
    },
    allowAddingNewNotes: {
      type: Boolean,
      default: false,
    },
    editorMaxLength: {
      type: Number,
      default: undefined,
    },
    posthogNoCapture: {
      type: Boolean,
      default: false,
    },
  },

  mixins: [TimeMixin],

  data() {
    return {
      body: '',
      filterText: '',
      selectedProjectPhases: [],
      sortDescending: false,
      additionalRows: 0,
      addNote: false,
      savingNote: false,
      addingNewNote: false,
      notesReady: false,
      shareWithEmployer: true,
      showEditModal: false,
      notes: [],
      editNote: {},
      projectPhaseOptions: [
        { id: 'scoping', name: 'Scoping' },
        { id: 'general', name: 'General' },
        { id: 'completed', name: 'Completed' },
      ],
    };
  },

  beforeMount() {
    this.getNotes();
  },

  watch: {
    userId() {
      this.getNotes();
    },
    programmeId() {
      this.getNotes();
    },
    customNotes(newNotes) {
      this.notes = newNotes;
      this.notesReady = true;
    },
  },

  computed: {
    hasInteractionPermission() {
      return this.$permissions.hasPermission('manage_user_notes') || this.isLearnerSupport;
    },

    validForm() {
      return Boolean(this.body);
    },

    disableSave() {
      return !this.validForm || this.body.length < 10 || this.savingNote;
    },

    totalRows() {
      return this.maxRows + this.additionalRows;
    },

    sortedNotes() {
      return sortObjectArray(this.notes, this.customSortBy || 'date_created', !this.sortDescending).slice(0, this.totalRows);
    },

    filteredNotes() {
      let filtered = this.sortedNotes;

      // Filter by text search
      if (this.filterText.length > 0) {
        const regOption = new RegExp(this.filterText, 'ig');
        filtered = filtered.filter(x => {
          if ((x.note && x.note.match(regOption))
              || (x.author_name && x.author_name.match(regOption))
              || (this.parseTimestamp(x.date_created) && this.parseTimestamp(x.date_created).match(regOption))
              || (this.parseTimestamp(x.date_updated) && this.parseTimestamp(x.date_updated).match(regOption))) {
            return true;
          }
          return false;
        });
      }

      // Filter by project phase (multi-select)
      if (this.selectedProjectPhases.length > 0) {
        filtered = filtered.filter(x => x.project_phase && this.selectedProjectPhases.includes(x.project_phase));
      }
      return filtered;
    },
  },

  methods: {
    toggleSort() {
      this.sortDescending = !this.sortDescending;
    },

    getNotes() {
      if (this.useCustomNotes) {
        this.$emit('custom-get-notes');
        return;
      }

      if (!this.userId || !this.programmeId) {
        this.notesReady = true;
        return;
      }
      this.$logger.info('Getting notes', { userId: this.userId, programmeId: this.programmeId }, true);
      this.notesReady = false;
      this.$http.get(`/api/profile/users/${this.userId}/programmes/${this.programmeId}/notes`).then(result => {
        this.notes = result.data;
        this.$logger.info('Got notes', { userId: this.userId, programmeId: this.programmeId });
      }).catch(error => {
        this.$logger.error('Error getting notes', { userId: this.userId, programmeId: this.programmeId }, error);
        this.$ktoast.showError(error);
      }).then(() => {
        this.notesReady = true;
        this.showEditModal = false;
      });
    },

    addNewNote() {
      if (this.useCustomNotes) {
        this.$emit('custom-add-note', this.body);
        this.body = '';
        this.savingNote = false;
        this.addingNewNote = false;
        return;
      }

      this.savingNote = true;
      this.$logger.info('Adding new note', undefined, true);
      this.$http.post(`/api/profile/users/${this.userId}/programmes/${this.programmeId}/notes`, {
        body: this.body,
        share_with_employer: this.shareWithEmployer,
      }).then(() => {
        this.$logger.info('New note added');
        this.$ktoast.success('New note added');
        this.$emit('note-added');
        this.body = '';
        this.shareWithEmployer = true;
        this.getNotes();
        this.savingNote = false;
        this.addingNewNote = false;
      }).catch(err => {
        this.$logger.error('Error adding new note', undefined, err);
        this.$ktoast.showError(err);
        this.savingNote = false;
      });
    },

    openEditModal(note) {
      this.editNote = note;
      this.showEditModal = true;
    },

    close() {
      this.showEditModal = false;
      this.editNote = {};
    },

    handleDeleteNote(noteId) {
      this.$emit('custom-delete-note', noteId);
      this.savingNote = false;
      this.addingNewNote = false;
    },

    handleEditNote(note) {
      this.$emit('custom-edit-note', note.noteId, note.body);
      this.savingNote = false;
      this.addingNewNote = false;
    },
  },
};
</script>
